import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../../components/PageTitle";
import { toggleMenu } from "../../utils/helpers";
import statusOptions from "../../options/approvalStatusOptions";
import NoRecordsAvailable from "../../components/NoRecordsAvailable";
import ApproveWFHTimesheetModal from "./ApproveWFHTimesheetModal";
import {
  API_APPROVAL_DASHBOARD_EMPLOYEES,
  GET_WFH_TIMESHEETS,
} from "../../config/Endpoints";
import Cookies from "js-cookie";
import approvalStatusOptions from "../../options/approvalStatusOptions";
import { formatToDateOnly, displayFormatDate } from "../../utils/helpers";
import { useEmployeeNamesListStore } from "../../stores/employeeNamesListStore.js";
import { TailSpin, ThreeDots } from "react-loader-spinner";
import axios from "axios";
import { showError } from "../../v2/utils/toast.ts";
import { Card } from "../../v2/components/shared/card/card.tsx";
import { FiEye } from "react-icons/fi";
import { Filter as FilterIcon } from "lucide-react";
import {
  Select,
  DateRange,
  Button,
  Badge,
  ResponsiveDataTable,
} from "../../v2/components/ui/index.ts";
import PageShell from "../../v2/components/shared/page-shell/page-shell.tsx";

const LISTING_STATUS_FALLBACK = "PENDING";

function isSkippedStatus(status) {
  return String(status ?? "").toUpperCase() === "SKIPPED";
}

/**
 * Status for the listing column: approval row for logged-in approver only.
 * Never managerStatus; never show SKIPPED on UI. If chosen row is SKIPPED and
 * actionTaken === "already", inherit first non-SKIPPED status on same projectId.
 * Otherwise no usable status → PENDING.
 */
function getStatusForLoggedInApprover(item, loginEmployeeId) {
  if (loginEmployeeId == null || Number.isNaN(loginEmployeeId)) {
    return LISTING_STATUS_FALLBACK;
  }

  const approvalsArr = item.approvals ?? [];
  const matches = approvalsArr.filter(
    (a) => a?.approverId != null && Number(a.approverId) === loginEmployeeId,
  );
  if (!matches.length) return LISTING_STATUS_FALLBACK;

  const nonSkipped = matches.filter((a) => !isSkippedStatus(a.status));
  const pool = nonSkipped.length ? nonSkipped : matches;

  const entries = item.entries ?? [];
  const entryProjectIds = new Set(
    entries.map((e) => e.projectId).filter((id) => id != null && id !== ""),
  );
  const hasMisc = entries.some(
    (e) => e.titleType === "OTHERS" || e.projectId == null,
  );

  let chosen;
  if (pool.length === 1) {
    chosen = pool[0];
  } else {
    const narrowed = pool.filter(
      (a) =>
        (a.projectId != null && entryProjectIds.has(Number(a.projectId))) ||
        (hasMisc && (a.projectId == null || a.projectId === "")),
    );
    const usePool = narrowed.length ? narrowed : pool;
    if (usePool.length === 1) {
      chosen = usePool[0];
    } else {
      chosen =
        usePool.find(
          (a) => a.status === "APPROVED" || a.status === "REJECTED",
        ) ?? usePool[0];
    }
  }

  const raw = chosen?.status;
  if (isSkippedStatus(raw)) {
    if (chosen?.actionTaken === "already") {
      const sameProject = (item.approvals ?? []).filter((a) => {
        if (a === chosen) return false;
        const ap = a?.projectId ?? null;
        const cp = chosen?.projectId ?? null;
        return ap === cp || Number(ap) === Number(cp);
      });
      const nonSkippedSame = sameProject.filter(
        (a) => !isSkippedStatus(a.status),
      );
      if (nonSkippedSame.length) {
        const fromSameProject =
          nonSkippedSame.find(
            (a) => a.status === "APPROVED" || a.status === "REJECTED",
          ) ?? nonSkippedSame[0];
        const v = fromSameProject?.status;
        if (v && !isSkippedStatus(v)) return v;
      }
    }
    return LISTING_STATUS_FALLBACK;
  }
  return raw && String(raw).trim() !== "" ? raw : LISTING_STATUS_FALLBACK;
}

export const getViewerStatuses = (approvals, loggedInUserId) => {
  const myApprovals = approvals.filter((a) => a.approverId === loggedInUserId);

  const statuses = new Set();

  myApprovals.forEach((approval) => {
    // direct statuses
    if (
      approval.status === "APPROVED" ||
      approval.status === "REJECTED" ||
      approval.status === "PENDING"
    ) {
      statuses.add(approval.status);
    }

    // skipped because action already taken
    if (approval.status === "SKIPPED" && approval.actionTaken === "already") {
      const matchedApproval = approvals.find(
        (a) => a.projectId === approval.projectId && a.status !== "SKIPPED",
      );

      if (
        matchedApproval?.status === "APPROVED" ||
        matchedApproval?.status === "REJECTED" ||
        matchedApproval?.status === "PENDING"
      ) {
        statuses.add(matchedApproval.status);
      }
    }
  });

  return [...statuses];
};

function isSuperAdminUser(employee) {
  const roles = employee?.roles;
  if (!Array.isArray(roles)) return false;
  return roles.some((r) => String(r).trim().toUpperCase() === "SUPERADMIN");
}

/** Listing Status column: SUPERADMIN sees sheet managerStatus; others use approver rows. */
function getListingStatus(item, loginEmployeeId, isSuperAdmin) {
  if (isSuperAdmin) {
    const mgr = item?.managerStatus;
    return mgr && String(mgr).trim() !== "" ? mgr : LISTING_STATUS_FALLBACK;
  }
  return getStatusForLoggedInApprover(item, loginEmployeeId);
}

function normalizeTimesheetRows(content, approverId) {
  if (!Array.isArray(content)) return [];

  const hasApprovalDashboardGroups = content.some((item) =>
    Array.isArray(item?.employees),
  );

  if (!hasApprovalDashboardGroups) {
    return content.map((employee) => {
      const status = employee.status || LISTING_STATUS_FALLBACK;
      return {
        ...employee,
        id: employee.timesheetId ?? employee.id,
        employeeId: employee.employeeId,
        employeeName: employee.employeeName,
        date: employee.timesheetDate ?? employee.date,
        managerStatus: status,
        _fromApprovalDashboard: true,
        entries: Array.isArray(employee.entries) ? employee.entries : [],
        approvals: Array.isArray(employee.approvals)
          ? employee.approvals
          : [
              {
                approverId,
                status,
                projectId: employee.projectId ?? null,
              },
            ],
      };
    });
  }

  return content.flatMap((group) =>
    (group.employees ?? []).map((employee) => {
      const status = employee.status || LISTING_STATUS_FALLBACK;
      return {
        ...employee,
        id: employee.timesheetId ?? employee.id,
        employeeId: employee.employeeId,
        employeeName: employee.employeeName,
        date: employee.timesheetDate,
        managerStatus: status,
        _fromApprovalDashboard: true,
        entries: [],
        approvals: [
          {
            approverId: group.approverId,
            approverName: group.approverName,
            status,
            projectId: employee.projectId ?? null,
          },
        ],
      };
    }),
  );
}

const ApproveWFHTimesheets = ({ title, description }) => {
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [data, setData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [timesheet, setTimesheet] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const accessToken = Cookies.get("accessToken");
  const [totalElements, setTotalElements] = useState(0);
  const {
    employeeList,
    loading: employeesLoading,
    fetchEmployees,
  } = useEmployeeNamesListStore();

  const { loginEmployeeId, isSuperAdmin } = useMemo(() => {
    try {
      const userData = localStorage.getItem("userData");
      const employee = userData ? JSON.parse(userData) : null;
      const id = employee?.id;
      if (id == null || id === "") {
        return {
          loginEmployeeId: null,
          isSuperAdmin: isSuperAdminUser(employee),
        };
      }
      const n = Number(id);
      return {
        loginEmployeeId: Number.isNaN(n) ? null : n,
        isSuperAdmin: isSuperAdminUser(employee),
      };
    } catch {
      return { loginEmployeeId: null, isSuperAdmin: false };
    }
  }, []);

  const employeeSelectOptions = useMemo(
    () => [
      ...employeeList.map((o) => ({ value: String(o.value), label: o.label })),
    ],
    [employeeList],
  );

  const statusSelectOptions = useMemo(
    () => [...statusOptions.map((o) => ({ value: o.value, label: o.label }))],
    [statusOptions],
  );

  useEffect(() => {
    fetchEmployees();
  }, []);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const fetchTimesheets = async (params = {}) => {
    if (loginEmployeeId == null) {
      setData([]);
      setTotalElements(0);
      return;
    }

    setLoading(true);
    try {
      const query = new URLSearchParams();

      if (params.employeeId) query.append("employeeId", params.employeeId);
      if (params.status) query.append("status", params.status);

      if (params.startDate && params.endDate) {
        query.append("fromDate", formatToDateOnly(params.startDate));
        query.append("toDate", formatToDateOnly(params.endDate));
      }

      query.append("page", currentPage - 1);
      query.append("size", limit);
      query.append("approverStatus", "true");

      const res = await axios.get(
        `${API_APPROVAL_DASHBOARD_EMPLOYEES(loginEmployeeId)}?${query.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      );

      setData(normalizeTimesheetRows(res.data?.content, loginEmployeeId));
      setTotalElements(res.data?.totalElements || 0);
    } catch (error) {
      setData([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (employeesLoading || !employeeList.length) return;
    if (!selectedEmployee && !selectedStatus && !startDate && !endDate) {
      if (hasInitiallyLoaded) {
        setFilterLoading(true);
        fetchTimesheets({}, true).finally(() => setFilterLoading(false));
      } else {
        fetchTimesheets({}, true).finally(() => setHasInitiallyLoaded(true));
      }
    }
  }, [
    employeesLoading,
    employeeList,
    selectedEmployee,
    selectedStatus,
    startDate,
    endDate,
  ]);

  const onDateChange = (dates) => {
    const [start, end] = dates;
    setStartDate(start || null);
    setEndDate(end || null);
  };

  const paginationMounted = React.useRef(false);
  useEffect(() => {
    if (!paginationMounted.current) {
      paginationMounted.current = true;
      return;
    }
    fetchTimesheets({
      employeeId: selectedEmployee || undefined,
      status: selectedStatus || undefined,
      startDate,
      endDate,
    });
  }, [currentPage, limit]);

  useEffect(() => {
    if (!data?.length || !employeeList.length) {
      setTableData([]);
      return;
    }

    const mappedTableData = data
      .filter((item) => {
        if (!selectedStatus) return true;
        if (item._fromApprovalDashboard) {
          return item.managerStatus === selectedStatus;
        }

        if (isSuperAdmin) {
          return item.managerStatus === selectedStatus;
        }

        const viewerStatuses = getViewerStatuses(
          item.approvals,
          loginEmployeeId,
        );

        return viewerStatuses.includes(selectedStatus);
      })
      .map((item) => {
        const employee = employeeList.find(
          (emp) => String(emp.value) === String(item.employeeId),
        );

        const viewerStatuses = getViewerStatuses(
          item.approvals,
          loginEmployeeId,
        );

        let badgeStatus = "PENDING";

        if (isSuperAdmin) {
          badgeStatus = item.managerStatus || "PENDING";
        } else if (selectedStatus) {
          badgeStatus = selectedStatus;
        } else {
          if (viewerStatuses.includes("PENDING")) {
            badgeStatus = "PENDING";
          } else if (viewerStatuses.includes("REJECTED")) {
            badgeStatus = "REJECTED";
          } else if (viewerStatuses.includes("APPROVED")) {
            badgeStatus = "APPROVED";
          }
        }

        return {
          employeeName: employee?.label || item.employeeName || item.employeeId,
          date: item.date,
          status: badgeStatus,
          _original: item,
        };
      });

    setTableData(mappedTableData);
  }, [data, employeeList, loginEmployeeId, isSuperAdmin, selectedStatus]);

  /** Distinct employees on the current page — demo of `ResponsiveDataTable` `tableFooter`. */
  const employeesCountThisPage = useMemo(() => {
    const ids = new Set();
    for (const row of tableData) {
      const id = row._original?.employeeId;
      if (id != null && id !== "") ids.add(String(id));
    }
    return ids.size;
  }, [tableData]);

  const handleFilter = () => {
    setCurrentPage(1);
    setFilterLoading(false);
    fetchTimesheets({
      employeeId: selectedEmployee || undefined,
      status: selectedStatus || undefined,
      startDate,
      endDate,
    });
  };

  const handleViewTimesheet = useCallback(async (request) => {
    if (Array.isArray(request.entries) && request.entries.length > 0) {
      setTimesheet(request.entries);
      setSelectedRequest(request);
      setOpenModal(true);
      return;
    }

    const timesheetId = request.timesheetId ?? request.id;
    if (timesheetId == null) {
      showError("Timesheet id is missing for this row.");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.get(GET_WFH_TIMESHEETS, {
        params: { id: timesheetId },
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const responseData = res.data;
      const timesheetData = Array.isArray(responseData)
        ? responseData[0]
        : Array.isArray(responseData?.content)
          ? responseData.content[0]
          : responseData;

      if (!timesheetData) {
        showError("Timesheet not found.");
        return;
      }

      setTimesheet(Array.isArray(timesheetData.entries) ? timesheetData.entries : []);
      setSelectedRequest(timesheetData);
      setOpenModal(true);
    } catch (error) {
      showError(
        error?.response?.data?.message || "Unable to load timesheet details.",
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const columns = useMemo(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        sortable: true,
        truncate: true,
      },
      {
        id: "date",
        header: "Date",
        accessor: "date",
        sortable: true,
        getSortValue: (row) => row.date,
        render: (row) => displayFormatDate(row.date),
        getTitleText: (row) => (row.date != null ? String(row.date) : ""),
      },
      {
        id: "status",
        header: "Status",
        accessor: "status",
        sortable: true,
        render: (row) => {
          const opt = approvalStatusOptions.find((s) => s.value === row.status);
          const variantMap = {
            success: "success",
            warning: "warning",
            danger: "danger",
          };
          return (
            <Badge variant={variantMap[opt?.type] ?? "neutral"}>
              {opt?.label || row.status}
            </Badge>
          );
        },
      },
      {
        id: "action",
        header: "Action",
        accessor: "Action",
        sortable: false,
        render: (row) => (
          <a
            className="action-btn--view"
            onClick={() => handleViewTimesheet(row._original)}
          >
            <FiEye size={14} />
            View
          </a>
        ),
      },
    ],
    [handleViewTimesheet],
  );

  return (
    <>
      <PageShell>
        {employeesLoading && (
          <div className="overlay">
            <div className="mt-5">
              <TailSpin
                height="100"
                width="100"
                radius="1"
                color="blue"
                ariaLabel="tail-spin-loading"
                wrapperStyle={{}}
                wrapperClassName=""
                visible={true}
              />
            </div>
          </div>
        )}

        <Card>
          <div className="filters d-flex gap-2 mb-3 flex-wrap align-items-end">
            <div className=" flex-grow-1">
              <Select
                label="Employee Name"
                options={employeeSelectOptions}
                placeholder="Select Employee Name"
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                isClearable
                searchable
              />
            </div>

            <div
              className="filter flex-grow-1"
              style={{ minWidth: 0, flex: "1 1 200px", maxWidth: "100%" }}
            >
              <DateRange
                label="Date"
                startDate={startDate}
                endDate={endDate}
                onChange={onDateChange}
                placeholderText="Select Date Range"
              />
            </div>

            <div
              className="filter flex-grow-1"
              style={{ minWidth: 0, flex: "1 1 200px", maxWidth: "100%" }}
            >
              <Select
                label="Status"
                options={statusSelectOptions}
                placeholder="Select Status"
                value={selectedStatus}
                onChange={setSelectedStatus}
                isClearable
              />
            </div>
            <Button
              type="button"
              variant="accent"
              onClick={handleFilter}
              disabled={loading}
              startIcon={<FilterIcon size={14} aria-hidden />}
            >
              Apply Filter
            </Button>
          </div>

          {filterLoading || (loading && !employeesLoading) ? (
            <div className="d-flex justify-content-center">
              <ThreeDots
                height="50"
                width="80"
                radius="9"
                color="var(--color-accent-500)"
              />
            </div>
          ) : !loading && !employeesLoading && tableData.length === 0 ? (
            <NoRecordsAvailable />
          ) : !loading && !employeesLoading ? (
            <ResponsiveDataTable
              columns={columns}
              data={tableData}
              aria-label="Approve timesheets"
              serialNumber
              page={currentPage}
              pageSize={limit}
              totalItems={totalElements}
              onPageChange={handlePageChange}
              onPageSizeChange={(size) => {
                setLimit(size);
                setCurrentPage(1);
              }}
              pageSizeOptions={[5, 10, 20, 30, 40]}
              totalLabel={
                <>
                  Total Requests: <strong>{totalElements}</strong>
                </>
              }
            />
          ) : null}
        </Card>
      </PageShell>
      <ApproveWFHTimesheetModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        timesheet={timesheet}
        selectedRequest={selectedRequest}
        onActionComplete={() => fetchTimesheets({}, true)}
      />
    </>
  );
};

export default ApproveWFHTimesheets;
